/** Composants métier du DS : stock, conditionnement, quantité, tableau, étapes, pagination, onglets, toasts, logo. */
import { createContext, useCallback, useContext, useId, useMemo, useState, type ReactNode } from 'react';
import { Minus, Plus } from 'lucide-react';
import { Alert, Icon, cx, type TonAlerte } from './base';
import logotypeCouleur from '../../source-v1.1/logos/myron-logotype-couleur.svg';
import logotypeCreme from '../../source-v1.1/logos/myron-logotype-creme.svg';
import monogrammeCouleur from '../../source-v1.1/logos/myron-monogramme-couleur.svg';

export type EtatStock = 'DISPONIBLE' | 'FAIBLE' | 'SUR_COMMANDE' | 'RUPTURE';
const STOCK: Record<EtatStock, { classe: string; libelle: string }> = {
  DISPONIBLE: { classe: 'dispo', libelle: 'Disponible' },
  FAIBLE: { classe: 'faible', libelle: 'Stock faible' },
  SUR_COMMANDE: { classe: 'commande', libelle: 'Sur commande' },
  RUPTURE: { classe: 'rupture', libelle: 'Rupture' },
};
/** Forme + couleur + texte : jamais la couleur seule. */
export function StockIndicator({ etat, libelle }: { etat: EtatStock; libelle?: string }) {
  const s = STOCK[etat];
  return (
    <span className={cx('my-stock', `my-stock--${s.classe}`)}>
      <span className="my-stock__forme" aria-hidden />
      {libelle ?? s.libelle}
    </span>
  );
}

export interface OptionPack {
  valeur: string;
  nom: string;
  detail: string;
  desactive?: boolean;
}
export function PackSelector({ legende, options, valeur, onChange, nom }: { legende: string; options: OptionPack[]; valeur: string; onChange: (v: string) => void; nom?: string }) {
  const id = useId();
  return (
    <fieldset className="my-pack" style={{ gridTemplateColumns: `repeat(${Math.min(options.length, 3)}, minmax(0, 1fr))` }}>
      <legend>{legende}</legend>
      {options.map((o) => (
        <label key={o.valeur}>
          <input type="radio" name={nom ?? id} value={o.valeur} checked={valeur === o.valeur} disabled={o.desactive} onChange={() => onChange(o.valeur)} />
          <span className="my-pack__nom">{o.nom}</span>
          <span className="my-pack__detail">{o.detail}</span>
        </label>
      ))}
    </fieldset>
  );
}

export function QuantityInput({ valeur, onChange, min = 1, max = 9999, pas = 1, label }: { valeur: number; onChange: (v: number) => void; min?: number; max?: number; pas?: number; label: string }) {
  const borne = (v: number) => Math.max(min, Math.min(max, v));
  return (
    <div className="my-qty" role="group" aria-label={label}>
      <button type="button" onClick={() => onChange(borne(valeur - pas))} disabled={valeur - pas < min} aria-label={`Diminuer ${label}`}>
        <Icon icon={Minus} />
      </button>
      <input
        type="number"
        inputMode="numeric"
        value={valeur}
        min={min}
        max={max}
        step={pas}
        aria-label={label}
        onChange={(e) => {
          const n = Number(e.target.value);
          if (Number.isFinite(n)) onChange(borne(Math.round(n)));
        }}
      />
      <button type="button" onClick={() => onChange(borne(valeur + pas))} disabled={valeur + pas > max} aria-label={`Augmenter ${label}`}>
        <Icon icon={Plus} />
      </button>
    </div>
  );
}

export interface Colonne<T> {
  cle: string;
  titre: ReactNode;
  rendu: (ligne: T) => ReactNode;
  num?: boolean;
  actions?: boolean;
}
export function Table<T>({ colonnes, lignes, cle, legende, onLigne }: { colonnes: Colonne<T>[]; lignes: T[]; cle: (l: T) => string; legende: string; onLigne?: (l: T) => void }) {
  return (
    <div className="my-table-box" tabIndex={0} role="region" aria-label={legende}>
      <table className="my-table">
        <caption className="my-sr">{legende}</caption>
        <thead>
          <tr>
            {colonnes.map((c) => (
              <th key={c.cle} scope="col" className={cx(c.num && 'num', c.actions && 'actions')}>{c.titre}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {lignes.map((l) => (
            <tr key={cle(l)} onClick={onLigne ? () => onLigne(l) : undefined} style={onLigne ? { cursor: 'pointer' } : undefined}>
              {colonnes.map((c) => (
                <td key={c.cle} className={cx(c.num && 'num', c.actions && 'actions')}>{c.rendu(l)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export interface Etape {
  titre: string;
  detail?: string;
  etat: 'fait' | 'encours' | 'avenir';
}
export function Steps({ etapes, label }: { etapes: Etape[]; label: string }) {
  return (
    <ol className="my-steps" aria-label={label}>
      {etapes.map((e) => (
        <li key={e.titre} className={e.etat === 'avenir' ? undefined : e.etat} aria-current={e.etat === 'encours' ? 'step' : undefined}>
          <span className="my-steps__point" aria-hidden />
          <strong>{e.titre}</strong>
          {e.detail && <span>{e.detail}</span>}
          <span className="my-sr">{e.etat === 'fait' ? 'terminée' : e.etat === 'encours' ? 'en cours' : 'à venir'}</span>
        </li>
      ))}
    </ol>
  );
}

export function Pagination({ page, total, taille, onPage }: { page: number; total: number; taille: number; onPage: (p: number) => void }) {
  const pages = Math.max(1, Math.ceil(total / taille));
  if (pages <= 1) return null;
  const liste = Array.from({ length: pages }, (_, i) => i + 1).filter((p) => p === 1 || p === pages || Math.abs(p - page) <= 1);
  const lien = (p: number, contenu: ReactNode, extra: Record<string, unknown> = {}) => (
    <a href={`?page=${p}`} onClick={(e) => { e.preventDefault(); if (p >= 1 && p <= pages) onPage(p); }} {...extra}>{contenu}</a>
  );
  return (
    <nav aria-label="Pagination">
      <ul className="my-pagination">
        <li>{lien(page - 1, '‹', { 'aria-label': 'Page précédente', 'aria-disabled': page <= 1 || undefined })}</li>
        {liste.map((p, i) => (
          <li key={p}>
            {i > 0 && p - liste[i - 1]! > 1 && <span aria-hidden style={{ padding: '0 4px' }}>…</span>}
            {lien(p, p, { 'aria-current': p === page ? 'page' : undefined, 'aria-label': `Page ${p}` })}
          </li>
        ))}
        <li>{lien(page + 1, '›', { 'aria-label': 'Page suivante', 'aria-disabled': page >= pages || undefined })}</li>
      </ul>
    </nav>
  );
}

export function Tabs<T extends string>({ onglets, actif, onChange, label }: { onglets: { id: T; libelle: string }[]; actif: T; onChange: (id: T) => void; label: string }) {
  return (
    <div className="my-onglets" role="tablist" aria-label={label}>
      {onglets.map((o) => (
        <button
          key={o.id}
          role="tab"
          type="button"
          aria-selected={actif === o.id}
          tabIndex={actif === o.id ? 0 : -1}
          onClick={() => onChange(o.id)}
          onKeyDown={(e) => {
            const i = onglets.findIndex((x) => x.id === actif);
            if (e.key === 'ArrowRight') onChange(onglets[(i + 1) % onglets.length]!.id);
            if (e.key === 'ArrowLeft') onChange(onglets[(i - 1 + onglets.length) % onglets.length]!.id);
          }}
        >
          {o.libelle}
        </button>
      ))}
    </div>
  );
}

interface Toast { id: number; ton: TonAlerte; titre: string; texte?: string }
const ToastCtx = createContext<(t: Omit<Toast, 'id'>) => void>(() => undefined);
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const notifier = useCallback((t: Omit<Toast, 'id'>) => {
    const id = Date.now() + Math.random();
    setToasts((l) => [...l, { ...t, id }]);
    setTimeout(() => setToasts((l) => l.filter((x) => x.id !== id)), 6000);
  }, []);
  const valeur = useMemo(() => notifier, [notifier]);
  return (
    <ToastCtx.Provider value={valeur}>
      {children}
      <div className="my-toasts" aria-live="polite">
        {toasts.map((t) => (
          <Alert key={t.id} ton={t.ton} titre={t.titre}>{t.texte}</Alert>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}
export const useToast = () => useContext(ToastCtx);

/** Logos officiels V1.1 — fichiers SVG fournis, jamais redessinés. */
export function Logo({ variante = 'couleur', hauteur = 32 }: { variante?: 'couleur' | 'creme' | 'monogramme'; hauteur?: number }) {
  const src = variante === 'creme' ? logotypeCreme : variante === 'monogramme' ? monogrammeCouleur : logotypeCouleur;
  return <img src={src} alt="MYRON" style={{ height: hauteur, width: 'auto' }} />;
}
